
import * as React from "react"
import * as DialogPrimitive from "@radix-ui/react-dialog"
import { X } from "lucide-react"

import { cn } from "../lib/utils"

const Dialog = DialogPrimitive.Root

const DialogTrigger = DialogPrimitive.Trigger

const DialogPortal = DialogPrimitive.Portal

const DialogClose = DialogPrimitive.Close

const DialogOverlay = React.forwardRef<
  React.ElementRef<typeof DialogPrimitive.Overlay>,
  React.ComponentPropsWithoutRef<typeof DialogPrimitive.Overlay>
>(({ className, ...props }, ref) => (
  <DialogPrimitive.Overlay
    ref={ref}
    className={cn(
      // Enter-only motion, and none under prefers-reduced-motion. There is no exit animation on purpose: Radix keeps
      // a closing dialog mounted until its exit animation ends, and that would leave two modals up for a moment
      // when one closes into the next (the one-modal invariant). Without an animation it unmounts in the same render.
      "fixed inset-0 z-50 bg-black/50 motion-safe:data-[state=open]:animate-in motion-safe:data-[state=open]:fade-in-0",
      className
    )}
    {...props}
  />
))
DialogOverlay.displayName = DialogPrimitive.Overlay.displayName

interface DialogContentProps extends React.ComponentPropsWithoutRef<typeof DialogPrimitive.Content> {
  /** Leave out the corner X when the dialog carries its own named Close control. */
  hideClose?: boolean
}

// Bounded on every width: 1rem gutters, at most 90vh (scrolls inside), rounded at all sizes.
// A long body (CloseOutVisit's missing-note list) must not push the title and buttons off a phone.
const DialogContent = React.forwardRef<
  React.ElementRef<typeof DialogPrimitive.Content>,
  DialogContentProps
>(({ className, children, hideClose = false, ...props }, ref) => (
  <DialogPortal>
    <DialogOverlay />
    <DialogPrimitive.Content
      ref={ref}
      aria-modal="true"
      className={cn(
        "fixed left-[50%] top-[50%] z-50 grid w-full max-w-[calc(100%-2rem)] max-h-[90vh] overflow-y-auto translate-x-[-50%] translate-y-[-50%] gap-4 border bg-background p-6 shadow-lg duration-200 motion-safe:data-[state=open]:animate-in motion-safe:data-[state=open]:fade-in-0 motion-safe:data-[state=open]:zoom-in-95 rounded-lg sm:max-w-lg",
        className
      )}
      {...props}
    >
      {children}
      {!hideClose && (
        <DialogPrimitive.Close className="absolute right-4 top-4 rounded-sm opacity-70 ring-offset-background transition-opacity hover:opacity-100 focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 disabled:pointer-events-none data-[state=open]:bg-accent data-[state=open]:text-muted-foreground">
          <X className="h-4 w-4" />
          <span className="sr-only">Close</span>
        </DialogPrimitive.Close>
      )}
    </DialogPrimitive.Content>
  </DialogPortal>
))
DialogContent.displayName = DialogPrimitive.Content.displayName

/**
 * The bare modal panel, for a surface that brings its own panel styling (the Tide shells' `.sb-panel`, `.mf-confirm`):
 * Radix gives it the focus trap, Escape, the inert background and focus return; the caller supplies the look. Compose it
 * inside `DialogPortal` with a `DialogOverlay`.
 */
const DialogPanel = React.forwardRef<
  React.ElementRef<typeof DialogPrimitive.Content>,
  React.ComponentPropsWithoutRef<typeof DialogPrimitive.Content>
>((props, ref) => <DialogPrimitive.Content ref={ref} aria-modal="true" {...props} />)
DialogPanel.displayName = "DialogPanel"

/**
 * Focus back to what opened the dialog. Radix returns focus on close only to a `DialogTrigger`; every modal here is
 * opened by a plain button elsewhere in the tree, so without this focus falls to <body> on close. Call it where the
 * `open` state lives (it records the focused element at the moment `open` turns true, before Radix moves focus in)
 * and pass the result to `onCloseAutoFocus` of the content/panel. It restores focus only when focus has fallen to
 * <body>; and if the page asked for focus somewhere else while the modal was still trapping it (the trap pulled it
 * back inside), that is where focus goes once the modal is gone, ahead of the opener.
 */
function useReturnFocus(open: boolean) {
  const opener = React.useRef<HTMLElement | null>(null)
  const asked = React.useRef<Element[]>([])
  React.useLayoutEffect(() => {
    if (open) opener.current = document.activeElement as HTMLElement | null
  }, [open])
  React.useEffect(() => {
    if (!open) return
    asked.current = []
    const onFocusIn = (e: FocusEvent) => {
      if (e.target instanceof Element) asked.current = [...asked.current.slice(-9), e.target]
    }
    window.addEventListener("focusin", onFocusIn, true)
    return () => window.removeEventListener("focusin", onFocusIn, true)
  }, [open])
  return React.useCallback((event: Event) => {
    event.preventDefault()
    const active = document.activeElement
    if (active && active !== document.body) return // the page already put focus somewhere on purpose
    const container = event.target instanceof Node ? event.target : null
    const wanted = [...asked.current]
      .reverse()
      .find((el) => el.isConnected && el !== document.body && !el.hasAttribute("data-radix-focus-guard") && !(container && container.contains(el)))
    const el = (wanted as HTMLElement | undefined) ?? opener.current
    if (el && el !== document.body && el.isConnected && typeof el.focus === "function") el.focus()
  }, [])
}

const DialogHeader = ({
  className,
  ...props
}: React.HTMLAttributes<HTMLDivElement>) => (
  <div
    className={cn(
      "flex flex-col space-y-1.5 text-center sm:text-left",
      className
    )}
    {...props}
  />
)
DialogHeader.displayName = "DialogHeader"

const DialogFooter = ({
  className,
  ...props
}: React.HTMLAttributes<HTMLDivElement>) => (
  <div
    className={cn(
      "flex flex-col-reverse gap-2 sm:flex-row sm:justify-end",
      className
    )}
    {...props}
  />
)
DialogFooter.displayName = "DialogFooter"

const DialogTitle = React.forwardRef<
  React.ElementRef<typeof DialogPrimitive.Title>,
  React.ComponentPropsWithoutRef<typeof DialogPrimitive.Title>
>(({ className, ...props }, ref) => (
  <DialogPrimitive.Title
    ref={ref}
    className={cn(
      "text-lg font-semibold leading-none tracking-tight",
      className
    )}
    {...props}
  />
))
DialogTitle.displayName = DialogPrimitive.Title.displayName

const DialogDescription = React.forwardRef<
  React.ElementRef<typeof DialogPrimitive.Description>,
  React.ComponentPropsWithoutRef<typeof DialogPrimitive.Description>
>(({ className, ...props }, ref) => (
  <DialogPrimitive.Description
    ref={ref}
    className={cn("text-sm text-muted-foreground", className)}
    {...props}
  />
))
DialogDescription.displayName = DialogPrimitive.Description.displayName

export {
  Dialog,
  DialogPortal,
  DialogOverlay,
  DialogTrigger,
  DialogClose,
  DialogContent,
  DialogPanel,
  useReturnFocus,
  DialogHeader,
  DialogFooter,
  DialogTitle,
  DialogDescription,
}
